import React, { useMemo } from "react";
import CreatableSelect from "react-select/creatable";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (value = "") => EMAIL_REGEX.test(String(value).trim());

const toUserOption = (user) => ({
  value: `user:${user.id}`,
  label: user.name || user.email,
  type: "user",
  userId: user.id,
});

const toEmailOption = (email) => ({
  value: `email:${email}`,
  label: email,
  type: "email",
});

export const recipientValueToOptions = (recipientValue = {}, userList = []) => {
  const userIds = recipientValue?.user_ids || [];
  const customEmails = recipientValue?.custom_emails || [];
  const userOptions = userIds
    .map((userId) => {
      const user = userList.find((item) => String(item.id) === String(userId));
      if (!user) {
        return {
          value: `user:${userId}`,
          label: `User #${userId}`,
          type: "user",
          userId,
        };
      }
      return toUserOption(user);
    })
    .filter(Boolean);

  const emailOptions = customEmails.map((email) => toEmailOption(email));
  return [...userOptions, ...emailOptions];
};

export const optionsToRecipientValue = (options = []) => {
  const user_ids = [];
  const custom_emails = [];

  options.forEach((option) => {
    if (option.type === "user" && option.userId != null) {
      user_ids.push(option.userId);
      return;
    }

    if (option.type === "email") {
      custom_emails.push(option.label);
      return;
    }

    if (String(option.value).startsWith("user:")) {
      user_ids.push(Number(String(option.value).replace("user:", "")));
      return;
    }

    if (String(option.value).startsWith("email:")) {
      custom_emails.push(String(option.value).replace("email:", ""));
    }
  });

  return {
    user_ids: [...new Set(user_ids)],
    custom_emails: [...new Set(custom_emails.map((email) => email.trim().toLowerCase()))],
  };
};

const parseCommaSeparatedEmails = (inputValue = "") =>
  inputValue
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

const JobEmailRecipientSelect = ({
  value = [],
  onChange,
  userOptions = [],
  placeholder = "Select users or enter emails",
  label = "Select Recipients",
  error = "",
  isDisabled = false,
}) => {
  const selectOptions = useMemo(
    () => userOptions.map((user) => toUserOption(user)),
    [userOptions]
  );

  const handleChange = (selectedOptions, actionMeta) => {
    const current = selectedOptions || [];

    if (actionMeta?.action === "create-option") {
      const createdLabel = actionMeta.option?.label || "";
      if (createdLabel.includes(",")) {
        const parsedEmails = parseCommaSeparatedEmails(createdLabel).filter(isValidEmail);
        const withoutCreated = current.filter(
          (option) => option.value !== actionMeta.option?.value
        );
        const merged = [...withoutCreated];

        parsedEmails.forEach((email) => {
          const emailOption = toEmailOption(email.toLowerCase());
          if (!merged.some((item) => item.value === emailOption.value)) {
            merged.push(emailOption);
          }
        });

        onChange?.(merged);
        return;
      }

      if (!isValidEmail(createdLabel)) {
        const withoutInvalid = current.filter(
          (option) => option.value !== actionMeta.option?.value
        );
        onChange?.(withoutInvalid);
        return;
      }

      const normalized = current.map((option) => {
        if (option.value === actionMeta.option?.value) {
          return toEmailOption(createdLabel.trim().toLowerCase());
        }
        return option;
      });
      onChange?.(normalized);
      return;
    }

    onChange?.(current);
  };

  const isValidNewOption = (inputValue) => {
    const trimmed = inputValue.trim();
    if (!trimmed) return false;
    if (trimmed.includes(",")) {
      return parseCommaSeparatedEmails(trimmed).every(isValidEmail);
    }
    return isValidEmail(trimmed);
  };

  const getNewOptionData = (inputValue) => {
    const trimmed = inputValue.trim();
    if (trimmed.includes(",")) {
      return {
        value: `draft:${trimmed}`,
        label: trimmed,
        type: "email",
      };
    }

    return toEmailOption(trimmed.toLowerCase());
  };

  const formatCreateLabel = (inputValue) => {
    const trimmed = inputValue.trim();
    if (trimmed.includes(",")) {
      return `Add ${parseCommaSeparatedEmails(trimmed).length} email(s)`;
    }
    return `Add "${trimmed}"`;
  };

  return (
    <div className="setup-recipient-select">
      <div className="floating-label-dropdown-wrapper">
        <span className="floating-label-dropdown-title">{label}</span>
        <CreatableSelect
          isMulti
          isDisabled={isDisabled}
          options={selectOptions}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          className="multi-select w-100"
          classNamePrefix="react-select"
          isSearchable
          isClearable={false}
          formatCreateLabel={formatCreateLabel}
          getNewOptionData={getNewOptionData}
          isValidNewOption={isValidNewOption}
          noOptionsMessage={() => "No users found. Type an email to add."}
        />
      </div>
      {error ? <div className="invalid-feedback d-block">{error}</div> : null}
      <div className="setup-recipient-hint">
        Select users from the list or type comma-separated email addresses.
      </div>
    </div>
  );
};

export default JobEmailRecipientSelect;
